<template>
  <div>
    <a-tree-select
      v-model="selectedValue"
      :tree-data="treeData"
      :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
      :placeholder="placeholder"
      :allow-clear="allowClear"
      :disabled="disabled"
      :multiple="multiple"
      :show-search="true"
      tree-node-filter-prop="title"
      :tree-checkable="multiple"
      :show-checked-strategy="SHOW_ALL"
      style="width: 100%"
      @change="handleChange"
      @search="handleSearch"
    >
      <template #title="{ title, isLeaf }">
        <span :style="{ fontWeight: isLeaf ? 'normal' : 'bold' }">{{ title }}</span>
      </template>
    </a-tree-select>
  </div>
</template>

<script>
import { dataApi } from '@/services/comm/memberManage'
import { XEUtils } from '@/utils/common/FormBase'
import { TreeSelect } from 'ant-design-vue'

export default {
  name: 'DeptTreeSelect',
  props: {
    value: {
      type: [String, Array],
      default: null
    },
    placeholder: {
      type: String,
      default: '请选择部门'
    },
    allowClear: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedValue: this.value,
      treeData: [],
      allDeptList: [],
      loading: false,
      SHOW_ALL: TreeSelect.SHOW_ALL
    }
  },
  watch: {
    value(val) {
      this.selectedValue = val
    }
  },
  created() {
    this.loadTreeData()
  },
  methods: {
    // 加载树数据
    async loadTreeData() {
      this.loading = true
      try {
        const res = await dataApi.getDeptList()
        this.loading = false
        if (res.code === 200 && res.data) {
          this.allDeptList = res.data
          // 保留所有部门，构建完整的树形结构
          this.buildTree(res.data)
        }
      } catch (error) {
        this.loading = false
        console.error('获取部门列表失败:', error)
      }
    },
    // 构建树结构
    buildTree(deptList) {
      // 转换为树形结构
      const tree = XEUtils.toArrayTree(deptList, {
        key: 'deptId',
        parentKey: 'parentId',
        children: 'children'
      })
      
      // 转换为 ant-design-vue TreeSelect 需要的格式
      this.treeData = this.convertToTreeSelectFormat(tree)
    },
    // 转换为 TreeSelect 格式
    convertToTreeSelectFormat(nodes) {
      return nodes.map(node => {
        const treeNode = {
          key: `dept_${node.deptId}`,
          value: `dept_${node.deptId}`,
          title: node.deptName,
          deptId: node.deptId,
          deptName: node.deptName,
          dealerId: node.dealerId,
          dealerName: node.dealerName,
          isDealer: node.isDealer,
          isRegion: node.isRegion
        }
        
        if (node.children && node.children.length > 0) {
          treeNode.children = this.convertToTreeSelectFormat(node.children)
          // 有子节点的也可以选择
          treeNode.selectable = true
        } else {
          treeNode.isLeaf = true
        }
        
        return treeNode
      })
    },
    // 处理选择变化
    handleChange(value) {
      this.selectedValue = value
      
      if (this.multiple) {
        // 多选：返回选中的部门列表和所有部门ID（包括子部门）
        const selectedDepts = []
        const allDeptIds = new Set()
        
        if (value && value.length > 0) {
          value.forEach(val => {
            // 处理可能是字符串或对象的情况
            let keyValue = val
            if (typeof val === 'object' && val.value) {
              keyValue = val.value
            }
            
            if (keyValue && typeof keyValue === 'string' && keyValue.startsWith('dept_')) {
              const dept = this.findDeptByKey(keyValue)
              if (dept) {
                selectedDepts.push(dept)
                // 收集该部门及其所有子部门的ID
                this.collectDeptIdsRecursive(dept.deptId, allDeptIds)
              }
            }
          })
        }
        
        const deptIdsArray = Array.from(allDeptIds)
        console.log('DeptTreeSelect - 选中的部门IDs:', deptIdsArray)
        
        this.$emit('input', value)
        this.$emit('change', selectedDepts)
        // 触发 deptIdsChange 事件，返回所有部门ID（包括子部门）
        this.$emit('deptIdsChange', deptIdsArray)
      } else {
        // 单选：返回选中的部门
        let keyValue = value
        if (typeof value === 'object' && value.value) {
          keyValue = value.value
        }
        
        if (keyValue && typeof keyValue === 'string' && keyValue.startsWith('dept_')) {
          const dept = this.findDeptByKey(keyValue)
          this.$emit('input', value)
          this.$emit('change', dept)
        } else {
          this.$emit('input', null)
          this.$emit('change', null)
        }
      }
    },
    // 递归收集部门ID（包括子部门）
    collectDeptIdsRecursive(deptId, deptIds) {
      // 添加当前部门ID
      deptIds.add(deptId)
      
      // 查找所有子部门
      const children = this.allDeptList.filter(d => d.parentId === deptId)
      children.forEach(child => {
        this.collectDeptIdsRecursive(child.deptId, deptIds)
      })
    },
    // 在树中查找部门
    findDeptByKey(key) {
      const deptId = parseInt(key.replace('dept_', ''))
      const dept = this.allDeptList.find(d => d.deptId === deptId)
      if (dept) {
        return {
          deptId: dept.deptId,
          deptName: dept.deptName,
          dealerId: dept.dealerId,
          dealerName: dept.dealerName
        }
      }
      return null
    },
    // 搜索
    handleSearch() {
      // 可以在这里添加搜索逻辑
    },
    // 刷新数据
    refresh() {
      this.loadTreeData()
    }
  }
}
</script>

<style lang="less" scoped>
</style>
